import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { X, Save, Plus, Trash2, RotateCcw, Upload, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Tour } from '../../hooks/useTours'
import type { CreateTourData } from '../../services/tourService'
import { TourService } from '../../services/tourService'
import { useHotels, getHotelsByCity, getLocalizedText as getHotelLocalizedText } from '../../hooks/useHotels'
import { useLanguage } from '../../contexts/LanguageContext'
import { uploadImage, uploadMultipleImages } from '../../lib/storage'

// Cities data with translations (sorted alphabetically by English name)
const CITIES = [
  { en: 'Baghdad', ar: 'بغداد', ur: 'بغداد', fa: 'بغداد' },
  { en: 'Balad', ar: 'بلد', ur: 'بلاد', fa: 'بلاد' },
  { en: 'Hllah', ar: 'حلة', ur: 'ہلہ', fa: 'حلاله' },
  { en: 'Karbala', ar: 'كربلاء', ur: 'کربلا', fa: 'کربلا' },
  { en: 'Kazmain', ar: 'كاظمين', ur: 'کاظمین', fa: 'کازمان' },
  { en: 'Kufa', ar: 'الكوفة', ur: 'کوفہ', fa: 'کوفه' },
  { en: 'Lahore', ar: 'لاهور', ur: 'لاہور', fa: 'لاهور' },
  { en: 'Mashhad', ar: 'مشهد', ur: 'مشہد', fa: 'مشهد' },
  { en: 'Musayiyab', ar: 'المسيب', ur: 'مصائب', fa: 'مسیب' },
  { en: 'Najaf', ar: 'النجف', ur: 'نجف', fa: 'نجف' },
  { en: 'Neshapur', ar: 'نيسابور', ur: 'نیشاپور', fa: 'نیشابور' },
  { en: 'Qom', ar: 'قم', ur: 'قم', fa: 'قم' },
  { en: 'Samara', ar: 'سامراء', ur: 'سامرہ', fa: 'سامره' },
  { en: 'Tehran', ar: 'طهران', ur: 'تہران', fa: 'تهران' }
]

interface TourFormProps {
  tour?: Tour | null
  isOpen: boolean
  onClose: () => void
  onSave: (tourData: CreateTourData) => Promise<void>
  onDelete?: (tourId: string) => Promise<void>
  onRestore?: (tourId: string) => Promise<void>
  isLoading?: boolean
}

export function TourForm({ tour, isOpen, onClose, onSave, onDelete, onRestore, isLoading = false }: TourFormProps) {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const { hotels } = useHotels()
  const [formData, setFormData] = useState<CreateTourData>({
    title_en: '',
    title_ar: '',
    title_ur: '',
    title_fa: '',
    departure_date: '',
    return_date: '',
    duration: '',
    price: '',
    image: '',
    route: [],
    highlights_en: [''],
    highlights_ar: [''],
    highlights_ur: [''],
    highlights_fa: [''],
    description_en: '',
    description_ar: '',
    description_ur: '',
    description_fa: '',
    images: [],
    memories: []
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState<Record<string, boolean>>({})
  const mainImageInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (tour) {
      setFormData({
        title_en: tour.title.en,
        title_ar: tour.title.ar,
        title_ur: tour.title.ur,
        title_fa: tour.title.fa,
        departure_date: tour.departureDate,
        return_date: tour.returnDate,
        duration: tour.duration,
        price: tour.price,
        image: tour.image,
        route: tour.route,
        highlights_en: tour.highlights.en.length > 0 ? tour.highlights.en : [''],
        highlights_ar: tour.highlights.ar.length > 0 ? tour.highlights.ar : [''],
        highlights_ur: tour.highlights.ur.length > 0 ? tour.highlights.ur : [''],
        highlights_fa: tour.highlights.fa.length > 0 ? tour.highlights.fa : [''],
        description_en: tour.description.en,
        description_ar: tour.description.ar,
        description_ur: tour.description.ur,
        description_fa: tour.description.fa,
        images: tour.images || [],
        memories: tour.memories || []
      })
    } else {
      // Reset form for new tour
      setFormData({
        title_en: '',
        title_ar: '',
        title_ur: '',
        title_fa: '',
        departure_date: '',
        return_date: '',
        duration: '',
        price: '',
        image: '',
        route: [],
        highlights_en: [''],
        highlights_ar: [''],
        highlights_ur: [''],
        highlights_fa: [''],
        description_en: '',
        description_ar: '',
        description_ur: '',
        description_fa: '',
        images: [],
        memories: []
      })
    }
    setErrors({})
  }, [tour, isOpen])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title_en.trim()) newErrors.title_en = 'English title is required'
    if (!formData.title_ar.trim()) newErrors.title_ar = 'Arabic title is required'
    if (!formData.title_ur.trim()) newErrors.title_ur = 'Urdu title is required'
    if (!formData.title_fa.trim()) newErrors.title_fa = 'Farsi title is required'
    if (!formData.departure_date) newErrors.departure_date = 'Departure date is required'
    if (!formData.return_date) newErrors.return_date = 'Return date is required'
    if (!formData.duration.trim()) newErrors.duration = 'Duration is required'
    if (!formData.price.trim()) newErrors.price = 'Price is required'
    if (!formData.image.trim()) newErrors.image = 'Main image is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await onSave(formData)
    } catch (error) {
      console.error('Error saving tour:', error)
    }
  }

  const updateHighlights = (language: 'en' | 'ar' | 'ur' | 'fa', index: number, value: string) => {
    const field = `highlights_${language}` as keyof typeof formData
    const highlights = [...(formData[field] as string[])]
    highlights[index] = value
    setFormData(prev => ({ ...prev, [field]: highlights }))
  }

  const addHighlight = (language: 'en' | 'ar' | 'ur' | 'fa') => {
    const field = `highlights_${language}` as keyof typeof formData
    const highlights = [...(formData[field] as string[]), '']
    setFormData(prev => ({ ...prev, [field]: highlights }))
  }

  const removeHighlight = (language: 'en' | 'ar' | 'ur' | 'fa', index: number) => {
    const field = `highlights_${language}` as keyof typeof formData
    const highlights = (formData[field] as string[]).filter((_, i) => i !== index)
    setFormData(prev => ({ ...prev, [field]: highlights }))
  }

  // Check if any upload is in progress
  const isUploading = Object.values(uploading).some(value => value === true)

  const handleImageUpload = async (file: File, type: 'main' | 'tour' | 'memory', index?: number) => {
    const uploadKey = `${type}-${index !== undefined ? index : 'main'}`
    setUploading(prev => ({ ...prev, [uploadKey]: true }))
    
    const result = await uploadImage(file, 'tours')
    
    setUploading(prev => ({ ...prev, [uploadKey]: false }))
    
    if (result.error) {
      setErrors(prev => ({ ...prev, image: result.error || 'Upload failed' }))
      return
    }
    
    if (result.url) {
      if (type === 'main') {
        setFormData(prev => ({ ...prev, image: result.url! }))
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors.image
          return newErrors
        })
      } else if (type === 'tour' && index !== undefined) {
        const newImages = [...(formData.images || [])]
        newImages[index] = result.url
        setFormData(prev => ({ ...prev, images: newImages }))
      } else if (type === 'memory' && index !== undefined) {
        const newMemories = [...(formData.memories || [])]
        newMemories[index] = result.url
        setFormData(prev => ({ ...prev, memories: newMemories }))
      }
    }
  }

  const handleMultipleImageUpload = async (files: FileList | null, type: 'tour' | 'memory') => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const uploadKey = `multiple-${type}`
    setUploading(prev => ({ ...prev, [uploadKey]: true }))
    
    const result = await uploadMultipleImages(fileArray, 'tours')
    
    setUploading(prev => ({ ...prev, [uploadKey]: false }))
    
    if (result.errors.length > 0) {
      setErrors(prev => ({ ...prev, [`multiple_${type}`]: result.errors.join('; ') }))
    }
    
    if (result.urls.length > 0) {
      if (type === 'tour') {
        const newImages = [...(formData.images || []), ...result.urls]
        setFormData(prev => ({ ...prev, images: newImages }))
      } else if (type === 'memory') {
        const newMemories = [...(formData.memories || []), ...result.urls]
        setFormData(prev => ({ ...prev, memories: newMemories }))
      }
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[`multiple_${type}`]
        return newErrors
      })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        {/* Fixed Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-foreground">
              {tour ? 'Edit Tour' : 'Add New Tour'}
            </h2>
            {tour?.deletedAt && (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                Deleted
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="tour-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* English Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title (English) *
              </label>
              <input
                type="text"
                value={formData.title_en}
                onChange={(e) => setFormData(prev => ({ ...prev, title_en: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.title_en ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
                placeholder="Enter English title"
              />
              {errors.title_en && <p className="text-destructive text-sm mt-1">{errors.title_en}</p>}
            </div>

            {/* Arabic Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title (Arabic) *
              </label>
              <input
                type="text"
                value={formData.title_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, title_ar: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.title_ar ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
                placeholder="Enter Arabic title"
                dir="rtl"
              />
              {errors.title_ar && <p className="text-destructive text-sm mt-1">{errors.title_ar}</p>}
            </div>

            {/* Urdu Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title (Urdu) *
              </label>
              <input
                type="text"
                value={formData.title_ur}
                onChange={(e) => setFormData(prev => ({ ...prev, title_ur: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.title_ur ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
                placeholder="Enter Urdu title"
                dir="rtl"
              />
              {errors.title_ur && <p className="text-destructive text-sm mt-1">{errors.title_ur}</p>}
            </div>

            {/* Farsi Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title (Farsi) *
              </label>
              <input
                type="text"
                value={formData.title_fa}
                onChange={(e) => setFormData(prev => ({ ...prev, title_fa: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.title_fa ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
                placeholder="Enter Farsi title"
                dir="rtl"
              />
              {errors.title_fa && <p className="text-destructive text-sm mt-1">{errors.title_fa}</p>}
            </div>
          </div>

          {/* Tour Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Departure Date *
              </label>
              <input
                type="date"
                value={formData.departure_date}
                onChange={(e) => setFormData(prev => ({ ...prev, departure_date: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.departure_date ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
              />
              {errors.departure_date && <p className="text-destructive text-sm mt-1">{errors.departure_date}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Return Date *
              </label>
              <input
                type="date"
                value={formData.return_date}
                onChange={(e) => setFormData(prev => ({ ...prev, return_date: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.return_date ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
              />
              {errors.return_date && <p className="text-destructive text-sm mt-1">{errors.return_date}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Duration *
              </label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.duration ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
                placeholder="e.g., 10 days"
              />
              {errors.duration && <p className="text-destructive text-sm mt-1">{errors.duration}</p>}
            </div>


            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Price *
              </label>
              <input
                type="text"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg bg-input border ${
                  errors.price ? 'border-destructive' : 'border-border'
                } focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
                placeholder="e.g., $2,500"
              />
              {errors.price && <p className="text-destructive text-sm mt-1">{errors.price}</p>}
            </div>
          </div>

          {/* Main Image Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Main Image * (Max 2MB)
            </label>
            <div className="flex items-center gap-3">
              <input
                ref={mainImageInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleImageUpload(file, 'main')
                  }
                  // Reset input to allow selecting the same file again
                  if (mainImageInputRef.current) {
                    mainImageInputRef.current.value = ''
                  }
                }}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => mainImageInputRef.current?.click()}
                disabled={uploading['main-main']}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading['main-main'] ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Image
                  </>
                )}
              </button>
              {formData.image && (
                <div className="flex-1 flex items-center gap-2">
                  <img 
                    src={formData.image} 
                    alt="Main tour image" 
                    className="h-16 w-16 object-cover rounded-lg border border-border"
                  />
                  <span className="text-sm text-muted-foreground truncate flex-1">
                    {formData.image}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                    className="p-1 text-destructive hover:bg-destructive/10 rounded transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {errors.image && <p className="text-destructive text-sm mt-1">{errors.image}</p>}
          </div>

          {/* Route */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Tour Route</h3>
            <p className="text-sm text-muted-foreground">
              Add the cities and destinations for this tour
            </p>
            <div className="space-y-3">
              {(formData.route || []).map((routeItem: any, index: number) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 border border-border rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      From City
                    </label>
                    <select
                      value={routeItem.from?.en || ''}
                      onChange={(e) => {
                        const selectedCity = CITIES.find(city => city.en === e.target.value)
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { 
                          ...newRoute[index], 
                          from: selectedCity ? {
                            en: selectedCity.en,
                            ar: selectedCity.ar,
                            ur: selectedCity.ur,
                            fa: selectedCity.fa
                          } : { en: '', ar: '', ur: '', fa: '' }
                        }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    >
                      <option value="">Select City</option>
                      {CITIES.map((city) => (
                        <option key={city.en} value={city.en}>
                          {city.en}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      To City
                    </label>
                    <select
                      value={routeItem.to?.en || ''}
                      onChange={(e) => {
                        const selectedCity = CITIES.find(city => city.en === e.target.value)
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { 
                          ...newRoute[index], 
                          to: selectedCity ? {
                            en: selectedCity.en,
                            ar: selectedCity.ar,
                            ur: selectedCity.ur,
                            fa: selectedCity.fa
                          } : { en: '', ar: '', ur: '', fa: '' }
                        }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    >
                      <option value="">Select City</option>
                      {CITIES.map((city) => (
                        <option key={city.en} value={city.en}>
                          {city.en}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Vehicle (English)
                    </label>
                    <select
                      value={routeItem.vehicle?.en || ''}
                      onChange={(e) => {
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { 
                          ...newRoute[index], 
                          vehicle: { 
                            en: e.target.value,
                            ar: e.target.value === 'airplane' ? 'طائرة' : e.target.value === 'train' ? 'قطار' : 'حافلة',
                            ur: e.target.value === 'airplane' ? 'ہوائی جہاز' : e.target.value === 'train' ? 'ٹرین' : 'بس',
                            fa: e.target.value === 'airplane' ? 'هواپیما' : e.target.value === 'train' ? 'قطار' : 'اتوبوس'
                          } 
                        }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    >
                      <option value="">Select Vehicle</option>
                      <option value="airplane">Airplane</option>
                      <option value="train">Train</option>
                      <option value="bus">Bus</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={routeItem.date || ''}
                      onChange={(e) => {
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { ...newRoute[index], date: e.target.value }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={routeItem.duration || ''}
                      onChange={(e) => {
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { ...newRoute[index], duration: e.target.value }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      placeholder="e.g., 2 days"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Distance
                    </label>
                    <input
                      type="text"
                      value={routeItem.distance || ''}
                      onChange={(e) => {
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { ...newRoute[index], distance: e.target.value }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      placeholder="e.g., 500 km"
                    />
                  </div>
                  {/* Hotel Selection for "From" City */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Hotel in "{routeItem.from?.en || 'From City'}" (Optional)
                    </label>
                    <select
                      value={routeItem.hotelIdFrom || ''}
                      onChange={(e) => {
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { 
                          ...newRoute[index], 
                          hotelIdFrom: e.target.value || null 
                        }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    >
                      <option value="">No hotel selected</option>
                      {routeItem.from?.en && getHotelsByCity(hotels, routeItem.from.en, language).map((hotel) => (
                        <option key={hotel.id} value={hotel.id}>
                          {getHotelLocalizedText(hotel.name, language)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Hotel Selection for "To" City */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Hotel in "{routeItem.to?.en || 'To City'}" (Optional)
                    </label>
                    <select
                      value={routeItem.hotelIdTo || ''}
                      onChange={(e) => {
                        const newRoute = [...(formData.route || [])]
                        newRoute[index] = { 
                          ...newRoute[index], 
                          hotelIdTo: e.target.value || null 
                        }
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    >
                      <option value="">No hotel selected</option>
                      {routeItem.to?.en && getHotelsByCity(hotels, routeItem.to.en, language).map((hotel) => (
                        <option key={hotel.id} value={hotel.id}>
                          {getHotelLocalizedText(hotel.name, language)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        const newRoute = (formData.route || []).filter((_: any, i: number) => i !== index)
                        setFormData(prev => ({ ...prev, route: newRoute }))
                      }}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  route: [...(prev.route || []), { 
                    from: { en: '', ar: '', ur: '', fa: '' },
                    to: { en: '', ar: '', ur: '', fa: '' },
                    vehicle: { en: '', ar: '', ur: '', fa: '' },
                    date: '',
                    duration: '', 
                    distance: '',
                    hotelIdFrom: null,
                    hotelIdTo: null
                  }] 
                }))}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
              >
                <Plus className="h-4 w-4" />
                Add Route Segment
              </button>
            </div>
          </div>

          {/* Descriptions */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Descriptions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description (English)
                </label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Enter English description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description (Arabic)
                </label>
                <textarea
                  value={formData.description_ar}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_ar: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Enter Arabic description"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description (Urdu)
                </label>
                <textarea
                  value={formData.description_ur}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_ur: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Enter Urdu description"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description (Farsi)
                </label>
                <textarea
                  value={formData.description_fa}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_fa: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Enter Farsi description"
                  dir="rtl"
                />
              </div>
            </div>
          </div>

          {/* Highlights */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Highlights</h3>
            {(['en', 'ar', 'ur', 'fa'] as const).map((lang) => (
              <div key={lang}>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-foreground">
                    Highlights ({lang.toUpperCase()})
                  </label>
                  <button
                    type="button"
                    onClick={() => addHighlight(lang)}
                    className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                  >
                    <Plus className="h-4 w-4" />
                    Add Highlight
                  </button>
                </div>
                <div className="space-y-2">
                  {(formData[`highlights_${lang}` as keyof typeof formData] as string[]).map((highlight, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={highlight}
                        onChange={(e) => updateHighlights(lang, index, e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg bg-input border border-border focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                        placeholder={`Enter ${lang.toUpperCase()} highlight`}
                        dir={lang === 'ar' || lang === 'ur' || lang === 'fa' ? 'rtl' : 'ltr'}
                      />
                      <button
                        type="button"
                        onClick={() => removeHighlight(lang, index)}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Tour Images */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Tour Images (Max 2MB each)</h3>
            
            {/* Multiple Image Upload */}
            <div className="mb-4">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                multiple
                onChange={(e) => handleMultipleImageUpload(e.target.files, 'tour')}
                className="hidden"
                id="tour-multiple-images"
                disabled={isUploading}
              />
              <label
                htmlFor="tour-multiple-images"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-block"
              >
                {uploading['multiple-tour'] ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Multiple Images
                  </>
                )}
              </label>
              {errors.multiple_tour && (
                <p className="text-sm text-destructive mt-2">{errors.multiple_tour}</p>
              )}
            </div>
            
            <div className="space-y-3">
              {(formData.images || []).map((image, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                  {image ? (
                    <>
                      <img 
                        src={image} 
                        alt={`Tour image ${index + 1}`} 
                        className="h-16 w-16 object-cover rounded-lg border border-border"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-muted-foreground truncate">{image}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newImages = (formData.images || []).filter((_, i) => i !== index)
                          setFormData(prev => ({ ...prev, images: newImages }))
                        }}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            handleImageUpload(file, 'tour', index)
                          }
                        }}
                        className="hidden"
                        id={`tour-image-${index}`}
                      />
                      <label
                        htmlFor={`tour-image-${index}`}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploading[`tour-${index}`] ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Upload Image
                          </>
                        )}
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const newImages = (formData.images || []).filter((_, i) => i !== index)
                          setFormData(prev => ({ ...prev, images: newImages }))
                        }}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, images: [...(prev.images || []), ''] }))}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
              >
                <Plus className="h-4 w-4" />
                Add Tour Image
              </button>
            </div>
          </div>

          {/* Memory Images */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Memory Images (Max 2MB each)</h3>
            <p className="text-sm text-muted-foreground">
              Add photos from completed tours to showcase the experience
            </p>
            
            {/* Multiple Image Upload */}
            <div className="mb-4">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                multiple
                onChange={(e) => handleMultipleImageUpload(e.target.files, 'memory')}
                className="hidden"
                id="memory-multiple-images"
                disabled={isUploading}
              />
              <label
                htmlFor="memory-multiple-images"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-block"
              >
                {uploading['multiple-memory'] ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Multiple Images
                  </>
                )}
              </label>
              {errors.multiple_memory && (
                <p className="text-sm text-destructive mt-2">{errors.multiple_memory}</p>
              )}
            </div>
            
            <div className="space-y-3">
              {(formData.memories || []).map((memory, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                  {memory ? (
                    <>
                      <img 
                        src={memory} 
                        alt={`Memory image ${index + 1}`} 
                        className="h-16 w-16 object-cover rounded-lg border border-border"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-muted-foreground truncate">{memory}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newMemories = (formData.memories || []).filter((_, i) => i !== index)
                          setFormData(prev => ({ ...prev, memories: newMemories }))
                        }}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            handleImageUpload(file, 'memory', index)
                          }
                        }}
                        className="hidden"
                        id={`memory-image-${index}`}
                      />
                      <label
                        htmlFor={`memory-image-${index}`}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploading[`memory-${index}`] ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Upload Image
                          </>
                        )}
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const newMemories = (formData.memories || []).filter((_, i) => i !== index)
                          setFormData(prev => ({ ...prev, memories: newMemories }))
                        }}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, memories: [...(prev.memories || []), ''] }))}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
              >
                <Plus className="h-4 w-4" />
                Add Memory Image
              </button>
            </div>
          </div>

          </form>
        </div>

        {/* Fixed Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-border">
          {/* Delete/Restore Actions */}
          {tour && (
            <div className="flex items-center gap-2">
              {tour.deletedAt ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (onRestore && tour.id) {
                      await onRestore(tour.id)
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  Restore Tour
                </button>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    if (onDelete && tour.id) {
                      await onDelete(tour.id)
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete Tour
                </button>
              )}
            </div>
          )}
          
          {/* Save/Cancel Actions */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="tour-form"
              disabled={isLoading || isUploading}
              className="flex items-center gap-2 px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : isUploading ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Uploading images...
                </>
              ) : (
                <>
                  <Save className="h-3 w-3" />
                  {tour ? 'Update Tour' : 'Create Tour'}
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
